import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { distinctUntilChanged } from 'rxjs/operators';
import { AgreementToPick } from 'src/app/models/dtos/agreement-to-pick-dto';
import { WorkerAgreementStatusEnum } from 'src/app/models/enums/worker-agreement-status-enum';

interface AgreementPickerForm {
  agreement: FormControl<AgreementToPick | null>;
}

@Component({
  selector: 'app-agreement-picker',
  templateUrl: './agreement-picker.component.html',
  styleUrls: ['./agreement-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AgreementPickerComponent {
  private formBuilder = inject(FormBuilder);

  agreements = input.required<AgreementToPick[]>();
  agreementSelection = model<AgreementToPick | null>(null);
  showInactive = signal(false);

  agreementForm: FormGroup<AgreementPickerForm>;

  filteredAgreements = computed(() =>
    this.agreements().filter(a =>
      this.showInactive() ? !this.isStillActive(a) : this.isStillActive(a)
    )
  );

  private get agreementFormControl(): FormControl<AgreementToPick | null> {
    return this.agreementForm.controls.agreement;
  }

  constructor() {
    this.agreementForm = this.buildAgreementFormGroup();
    this.onAgreementChange();

    effect(() => {
      const filtered = this.filteredAgreements();
      const currentAgreement = this.agreementFormControl.value;
      const currentAgreementInFilteredList = currentAgreement
        ? filtered.find((agreement) => agreement.Id === currentAgreement.Id) ?? null
        : null;
      const nextAgreement = currentAgreementInFilteredList ?? filtered[0] ?? null;

      if (nextAgreement?.Id === currentAgreement?.Id) {
        return;
      }

      this.agreementFormControl.setValue(nextAgreement);
    });
  }

  compareAgreements = (a: AgreementToPick | null, b: AgreementToPick | null): boolean => a?.Id === b?.Id;

  private isStillActive = (item: AgreementToPick): boolean => {
    if (item.AgreementStatusId === WorkerAgreementStatusEnum.Active) {
      return true;
    }

    if (item.AgreementStatusId !== WorkerAgreementStatusEnum.Terminated) {
      return false;
    }

    const terminationEndDate = this.toDate(item.AgreementTerminationEndDate);
    if (!terminationEndDate) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const terminationDateOnly = new Date(terminationEndDate);
    terminationDateOnly.setHours(0, 0, 0, 0);

    return terminationDateOnly >= today;
  };

  private toDate = (value: Date | string | null | undefined): Date | null => {
    if (!value) {
      return null;
    }

    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  private buildAgreementFormGroup = (): FormGroup<AgreementPickerForm> =>
    this.formBuilder.group<AgreementPickerForm>({
      agreement: new FormControl<AgreementToPick | null>(null, Validators.required),
    });

  private onAgreementChange = (): void => {
    this.agreementFormControl.valueChanges
      .pipe(
        takeUntilDestroyed(),
        distinctUntilChanged((previous, current) => previous?.Id === current?.Id),
      )
      .subscribe(agreement => this.agreementSelection.set(agreement));
  };
}
